import {
  type AccountInfo,
  BrowserCacheLocation,
  type Configuration,
  PublicClientApplication,
} from '@azure/msal-browser';

export type AccountType = 'external' | 'internal';

const ACCOUNT_TYPE_STORAGE_KEY = 'nz-app.account-type';
const PENDING_LOGOUT_STORAGE_KEY = 'nz-app.pending-logout';

const isAccountType = (value: null | string): value is AccountType =>
  value === 'internal' || value === 'external';

export const createTenantAuthority = (
  accountType: AccountType,
  tenantIds: { external: string; internal: string },
): string => {
  const tenantId = accountType === 'internal' ? tenantIds.internal : tenantIds.external;
  if (!tenantId || tenantId.includes('NZ_APP_')) {
    throw new Error(`Missing tenant configuration for ${accountType} account`);
  }
  return `https://login.microsoftonline.com/${tenantId}`;
};

export const getSelectedAccountType = (): AccountType | null => {
  const value = window.localStorage.getItem(ACCOUNT_TYPE_STORAGE_KEY);
  return isAccountType(value) ? value : null;
};

export const setSelectedAccountType = (accountType: AccountType): void => {
  window.localStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, accountType);
};

/**
 * Preserve the legacy internal-only experience when MSAL already has a
 * verifiable internal account, without creating Angular MSAL providers.
 */
export const migrateLegacyInternalAccount = async (options?: {
  clientId?: string;
  createClient?: (config: Configuration) => {
    getAllAccounts: () => AccountInfo[];
    initialize: () => Promise<void>;
  };
  internalTenantId?: string;
}): Promise<boolean> => {
  if (getSelectedAccountType()) return Promise.resolve(false);

  const clientId = options?.clientId ?? (import.meta.env['NZ_APP_CLIENT_ID'] as string);
  const internalTenantId =
    options?.internalTenantId ?? (import.meta.env['NZ_APP_TENANT_ID'] as string);
  if (
    !clientId ||
    clientId.includes('NZ_APP_') ||
    !internalTenantId ||
    internalTenantId.includes('NZ_APP_')
  ) {
    return Promise.resolve(false);
  }

  try {
    const createClient = options?.createClient ?? ((config) => new PublicClientApplication(config));
    const msal = createClient({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${internalTenantId}`,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
      },
    });
    await msal.initialize();
    const hasInternalAccount = msal
      .getAllAccounts()
      .some((account: AccountInfo) => account.tenantId === internalTenantId);
    if (!hasInternalAccount) return false;

    setSelectedAccountType('internal');
    return true;
  } catch {
    // Cache inspection must never prevent the public chooser from booting.
    return false;
  }
};

export const getSelectedAccount = (
  accounts: AccountInfo[],
  tenantIds: { external: string; internal: string },
): AccountInfo | null => {
  const accountType = getSelectedAccountType();
  if (!accountType) {
    return null;
  }

  const selectedTenantId = tenantIds[accountType];
  if (!selectedTenantId) {
    return null;
  }

  return accounts.find((account) => account.tenantId === selectedTenantId) ?? null;
};

export const clearSelectedAccountType = (): void => {
  window.localStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY);
};

export const markPendingLogout = (): void => {
  window.sessionStorage.setItem(PENDING_LOGOUT_STORAGE_KEY, 'true');
};

export const consumePendingLogout = (): boolean => {
  const pending = window.sessionStorage.getItem(PENDING_LOGOUT_STORAGE_KEY) === 'true';
  window.sessionStorage.removeItem(PENDING_LOGOUT_STORAGE_KEY);
  if (pending) {
    clearSelectedAccountType();
  }
  return pending;
};

export const accountTypeStorageKeys = {
  accountType: ACCOUNT_TYPE_STORAGE_KEY,
  pendingLogout: PENDING_LOGOUT_STORAGE_KEY,
} as const;
